import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ArrowRight, ArrowLeft, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation, useRTL } from '../utils/translations';

interface ConflictItem {
  id: string;
  name: string;
  type: string;
}

interface ConflictDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
  removedItems: ConflictItem[];
  addedItems: ConflictItem[];
  isDarkMode: boolean;
  applyLabel?: string;
}

export const ConflictDialog: React.FC<ConflictDialogProps> = ({
  isOpen,
  onClose,
  onApply,
  removedItems,
  addedItems,
  isDarkMode,
  applyLabel,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/[0.88] z-[100]"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={cn(
              "fixed left-[4%] right-[4%] md:left-1/2 md:right-auto top-1/2 -translate-y-1/2 md:-translate-x-1/2 w-[92%] md:w-full md:max-w-[872px] max-h-[90dvh] md:max-h-[90dvh] rounded-[var(--brand-dialog-radius)] shadow-2xl z-[101] overflow-hidden flex flex-col",
              isDarkMode ? "bg-[#111111]" : "bg-white"
            )}
          >
            {/* Header */}
            <div className={cn(
              "relative px-[24px] md:px-[47px] pb-0",
              isDarkMode ? "bg-[#111111]" : "bg-white"
            )}>
              <button
                onClick={onClose}
                className={cn(
                  "absolute top-[20px] w-[34px] h-[34px] rounded-full flex items-center justify-center transition-colors",
                  isRTL ? "left-[20px] md:left-[22px]" : "right-[20px] md:right-[22px]",
                  isDarkMode
                    ? "border border-white/20 text-white hover:bg-white/10"
                    : "border border-[#e2e2e2] text-[#282828] hover:bg-gray-100"
                )}
              >
                <X className="w-[14px] h-[14px]" />
              </button>

              <h2 className={cn(
                "text-[18px] md:text-[24px] font-semibold mt-[47px] mb-[8px]",
                isRTL ? "pl-10" : "pr-10",
                isDarkMode ? "text-white" : "text-[#282828]"
              )}>
                {t('staticTranslations.changesRequired', 'Changes Required')}
              </h2>
              <p className={cn(
                "text-[12px] md:text-[16px] leading-normal pb-[16px]",
                isDarkMode ? "text-white/60" : "text-[#333334]"
              )}>
                {t('staticTranslations.conflictDialog.description', 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.')}
              </p>
            </div>

            {/* Content Boxes */}
            <div className={cn(
              "px-[24px] md:px-[47px] py-[16px] flex flex-col md:flex-row items-stretch gap-4 md:gap-[14px] overflow-y-auto overflow-x-hidden flex-1 min-h-0",
              isDarkMode ? "bg-[#111111]" : "bg-white"
            )}>
              {/* Removed Items Section */}
              <div className={cn(
                "flex-1 rounded-[var(--brand-card-radius-lg)] p-[18px] border-[1.5px]",
                isDarkMode ? "bg-white/5 border-white/10" : "bg-[#eaeaea] border-[#e2e2e2]"
              )}>
                <p className={cn(
                  "text-[15px] md:text-[18px] font-semibold mb-[12px]",
                  isDarkMode ? "text-white" : "text-[#333334]"
                )}>
                  {(() => { const s = t('staticTranslations.itemsThatWillBeRemoved', 'Item(s) that will be removed'); const i = s.lastIndexOf(' '); return <>{s.slice(0, i + 1)}<span className="text-red-500">{s.slice(i + 1)}</span></>; })()}
                </p>
                <div className={cn(
                  "rounded-[var(--brand-card-radius)] px-[15px] py-[12px] min-h-[80px] md:min-h-[225px]",
                  isDarkMode ? "bg-[#1A1A1A] border border-white/10" : "bg-white border border-[#c4c4c4]"
                )}>
                  <ul className="space-y-1.5">
                    {removedItems.map((item, idx) => (
                      <li
                        key={idx}
                        className={cn(
                          "text-[13px] md:text-[16px] list-disc leading-normal",
                          isRTL ? "mr-[18px]" : "ml-[18px]",
                          isDarkMode ? "text-white/80" : "text-[#333334]"
                        )}
                      >
                        {item.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Arrow */}
              <div className={cn(
                "shrink-0 self-center",
                isDarkMode ? "text-white/30" : "text-[#282828]/40"
              )}>
                {isRTL ? (
                  <ArrowLeft className="hidden md:block w-5 h-5" />
                ) : (
                  <ArrowRight className="hidden md:block w-5 h-5" />
                )}
                <ArrowDown className="md:hidden w-5 h-5 mx-auto" />
              </div>

              {/* Added Items Section */}
              <div className={cn(
                "flex-1 rounded-[var(--brand-card-radius-lg)] p-[18px] border-[1.5px]",
                isDarkMode ? "bg-white/5 border-white/10" : "bg-[#eaeaea] border-[#e2e2e2]"
              )}>
                <p className={cn(
                  "text-[15px] md:text-[18px] font-semibold mb-[12px]",
                  isDarkMode ? "text-white" : "text-[#333334]"
                )}>
                  {(() => { const s = t('staticTranslations.itemsThatWillBeAdded', 'Item(s) that will be added'); const i = s.lastIndexOf(' '); return <>{s.slice(0, i + 1)}<span style={{ color: '#00b846' }}>{s.slice(i + 1)}</span></>; })()}
                </p>
                <div className={cn(
                  "rounded-[var(--brand-card-radius)] px-[15px] py-[12px] min-h-[80px] md:min-h-[225px]",
                  isDarkMode ? "bg-[#1A1A1A] border border-white/10" : "bg-white border border-[#c4c4c4]"
                )}>
                  <ul className="space-y-1.5">
                    {addedItems.map((item, idx) => (
                      <li
                        key={idx}
                        className={cn(
                          "text-[13px] md:text-[16px] list-disc leading-normal",
                          isRTL ? "mr-[18px]" : "ml-[18px]",
                          isDarkMode ? "text-white/80" : "text-[#333334]"
                        )}
                      >
                        {item.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={cn(
              "flex items-center justify-end gap-[12px] px-[24px] md:px-[47px] h-[77px] md:h-[83px] flex-shrink-0",
              isDarkMode ? "bg-[#1a1a1a]" : "bg-[#eaeaea]"
            )}>
              <button
                onClick={onClose}
                className={cn(
                  "h-[47px] px-[36px] min-w-[160px] rounded-[var(--brand-button-radius-sm)] text-[13px] md:text-[16px] font-semibold border transition-colors",
                  isDarkMode
                    ? "border-white/30 text-white bg-transparent hover:bg-white/10"
                    : "border-[var(--brand-accent)] text-[var(--brand-accent)] bg-[#eaeaea] hover:bg-blue-50"
                )}
              >
                {t('staticTranslations.common.cancel', 'Cancel')}
              </button>
              <button
                onClick={onApply}
                className="h-[47px] px-[36px] min-w-[160px] rounded-[var(--brand-button-radius-sm)] text-[13px] md:text-[16px] font-semibold bg-[var(--brand-accent)] text-white hover:bg-[var(--brand-accent-hover)] transition-colors"
              >
                {applyLabel ?? t('staticTranslations.applyChanges', 'Apply Changes')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
